import { Alert, Snackbar } from "@mui/material";
import { createContext, useCallback, useContext, useState } from "react";
import type { ReactNode } from "react";
import i18next from "~/i18n";
import ActionSnackbar, {
  type ActionSnackbarAction,
} from "../ActionSnackbar";

type Severity = "success" | "error" | "info" | "warning";

interface ActionToastOptions {
  severity?: Severity;
  title: string;
  description?: string;
  icon?: ReactNode;
  autoHideDuration?: number;
  showCloseAction?: boolean;
  primaryAction?: ActionSnackbarAction;
  secondaryAction?: ActionSnackbarAction;
}

interface ToastContextValue {
  showToast: (message: string, severity?: Severity) => void;
  showActionToast: (options: ActionToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue>({
  showToast: () => {},
  showActionToast: () => {},
});

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<Severity>("info");
  const [actionToast, setActionToast] = useState<{
    id: number;
    open: boolean;
    severity: Severity;
    title: string;
    description?: string;
    icon?: ReactNode;
    autoHideDuration: number;
    primaryAction?: ActionSnackbarAction;
    secondaryAction?: ActionSnackbarAction;
  }>({
    id: 0,
    open: false,
    severity: "info",
    title: "",
    description: "",
    autoHideDuration: 10000,
  });

  const showToast = useCallback((msg: string, sev: Severity = "info") => {
    setMessage(msg);
    setSeverity(sev);
    setOpen(true);
  }, []);

  const showActionToast = useCallback((options: ActionToastOptions) => {
    const defaultPrimaryAction: ActionSnackbarAction | undefined =
      options.primaryAction ||
      (options.showCloseAction === false
        ? undefined
        : {
            label: i18next.t("common.close"),
            onClick: () => {},
          });

    const wrapAction = (action?: ActionSnackbarAction): ActionSnackbarAction | undefined => {
      if (!action) return undefined;
      return {
        ...action,
        onClick: () => {
          setActionToast((prev) => ({ ...prev, open: false }));
          action.onClick();
        },
      };
    };

    setActionToast({
      id: Date.now(),
      open: true,
      severity: options.severity ?? "info",
      title: options.title,
      description: options.description,
      icon: options.icon,
      autoHideDuration: options.autoHideDuration ?? 10000,
      primaryAction: wrapAction(defaultPrimaryAction),
      secondaryAction: wrapAction(options.secondaryAction),
    });
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, showActionToast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
      <ActionSnackbar
        toastKey={actionToast.id}
        open={actionToast.open}
        severity={actionToast.severity}
        onClose={() =>
          setActionToast((prev) => ({
            ...prev,
            open: false,
          }))
        }
        icon={actionToast.icon}
        title={actionToast.title}
        description={actionToast.description}
        autoHideDuration={actionToast.autoHideDuration}
        primaryAction={actionToast.primaryAction}
        secondaryAction={actionToast.secondaryAction}
      />
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
